import { useEffect, useState, type ReactNode } from 'react';
import { cx } from '@linaria/atomic';
import { onlyText } from 'react-children-utilities';
import CopyToClipboard from 'react-copy-to-clipboard';
import { CopyIcon } from '../../icon/icons/copy';
import { CheckIconSmall } from '../../icon/icons-small/check-small';
import { IconButton } from '../icon-button/icon-button.component';
import { Text, type TextProps } from '../text/text.component';
import { useTooltip } from '../tooltip/use-tooltip';
import { noMarginStyle, textStyle, tooltipBaseStyle, tooltipCopiedStyle } from './copyable-text.styles';

export const CopyableText = ({
  children,
  tooltipText = 'Copy',
  tooltipSuccessText = 'Text copied!',
  text,
  trackingId,
  className,
  ...rest
}: TextProps & {
  text?: string;
  tooltipText?: string;
  tooltipSuccessText?: string;
  trackingId?: string;
}) => {
  const { triggerProps, tooltipProps, Tooltip } = useTooltip({ hoverDelay: 500 });
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (copied) {
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    }
  }, [tooltipProps.isOpen]);

  return (
    <>
      <CopyToClipboard
        text={text || onlyText(children as ReactNode)}
        onCopy={() => setCopied(true)}
        data-trackingid={trackingId}
      >
        <Text {...triggerProps} className={cx(textStyle, className)} {...rest}>
          {children}
        </Text>
      </CopyToClipboard>
      <Tooltip {...tooltipProps} className={cx(tooltipBaseStyle, copied && tooltipCopiedStyle)}>
        {copied ? (
          <CheckIconSmall className='copy-status-icon' size={16} />
        ) : (
          <CopyIcon className='copy-status-icon' size={16} />
        )}
        <Text className={noMarginStyle} color='white'>
          {copied ? tooltipSuccessText : tooltipText}
        </Text>
      </Tooltip>
    </>
  );
};

export const CopyableTextButton = ({
  children,
  tooltipText = 'Copy',
  tooltipSuccessText = 'Text copied!',
  text,
  trackingId,
  ...rest
}: TextProps & {
  text?: string;
  tooltipText?: string;
  tooltipSuccessText?: string;
  trackingId?: string;
}) => {
  const { triggerProps, tooltipProps, Tooltip } = useTooltip({ hoverDelay: 500 });
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (copied) {
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    }
  }, [tooltipProps.isOpen]);

  return (
    <>
      <Text {...rest}>{children}</Text>
      <CopyToClipboard
        text={text || onlyText(children as ReactNode)}
        onCopy={() => setCopied(true)}
        data-trackingid={trackingId}
      >
        <IconButton size='small' label='' {...triggerProps}>
          <CopyIcon size={16} />
        </IconButton>
      </CopyToClipboard>
      <Tooltip {...tooltipProps} className={cx(tooltipBaseStyle, copied && tooltipCopiedStyle)}>
        {copied ? (
          <CheckIconSmall className='copy-status-icon' size={16} />
        ) : (
          <CopyIcon className='copy-status-icon' size={16} />
        )}
        <Text className={noMarginStyle} color='white'>
          {copied ? tooltipSuccessText : tooltipText}
        </Text>
      </Tooltip>
    </>
  );
};
